import { createContext, type MouseEvent, type ReactNode, useCallback, useContext, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  PANEL_STEPS,
  type PanelHistoryState,
  panelPushState,
  type RecordKind,
  usePanelStack,
} from "@/components/ui/panel-stack-context";

/**
 * The app's record panel: `?deal=<id>` (or a reserved record kind's param, `PANEL_STEPS`) on any route
 * opens the record in the right panel, as a step of the panel stack (`components/ui/panel-stack.tsx`).
 * Opening pushes the param, so the panel is shareable, survives a refresh, and Back returns to the step
 * or page it was opened from. Each kind's drawer (`DealPanel`) renders its own record.
 *
 * An open from outside the panel (a board card, a row on a list) starts fresh, with no back link, even
 * over a step of the same kind already open — opening a different deal from the board must never show
 * a back link to the deal you were just looking at. A drill-in from inside the panel (Other deals, a
 * linked record) passes `open(id, { fromPanel: true })` to keep the back link to the step it came from. A drill-in
 * to a record already on the open path (deal A → deal B → A again, a to-do between or not) goes Back to it instead
 * of stacking it a second time, so the back link never flips between two records.
 * Opening a record of a DIFFERENT kind needs no flag: the step beneath (`panel-stack.tsx`'s own
 * `beneathEntry`) already names it, flag or not.
 */
const RECORD_KINDS: RecordKind[] = ["deal", "contact", "job"];

export interface RecordPanel {
  /** The record of this kind on the URL, or null. */
  id: string | null;
  /** `replace`: open in place of the current entry (a page that opens a linked record on arrival), so Back leaves
   *  the page as it would have, and Close clears the param in place. `fromPanel`: the open is a drill-in from
   *  inside this panel (Other deals, a linked record), so the back link names the step it came from. Without it
   *  (a board card, a row on a list outside the panel) the open starts a fresh stack with no back link, even
   *  while a step of the same kind is already on top. */
  open: (id: string, options?: { replace?: boolean; fromPanel?: boolean }) => void;
  close: () => void;
}

const RecordPanelContext = createContext<Record<RecordKind, RecordPanel> | null>(null);

export function RecordPanelProvider({ children }: { children: ReactNode }) {
  const deal = useRecordStep("deal");
  const contact = useRecordStep("contact");
  const job = useRecordStep("job");
  const value = useMemo(() => ({ deal, contact, job }), [deal, contact, job]);
  return <RecordPanelContext.Provider value={value}>{children}</RecordPanelContext.Provider>;
}

function useRecordStep(kind: RecordKind): RecordPanel {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const param = PANEL_STEPS[kind].param;
  const id = params.get(param);
  const panels = usePanelStack();

  // Opening the record already on top does nothing; one beneath another step (a to-do's deal chip) comes
  // back on top as a step of its own.
  const open = useCallback(
    (next: string, options?: { replace?: boolean; fromPanel?: boolean }) => {
      if (next === id && (!panels || panels.top === kind)) return;
      if (options?.fromPanel && panels?.goBackTo(kind, next)) return;
      const replace = !!options?.replace;
      setParams(
        (prev) => {
          const query = new URLSearchParams(prev);
          query.set(param, next);
          return query;
        },
        {
          replace,
          state: replace
            ? location.state
            : panelPushState(location.state, kind, options?.fromPanel ? panels?.topBack : undefined, { kind, id: next }),
        },
      );
    },
    [id, kind, param, panels, location.state, navigate, setParams],
  );

  // A record this panel pushed is closed by going back off its entry; a deep-linked one clears in place.
  const close = useCallback(() => {
    const history = location.state as PanelHistoryState | null;
    if (!id) return;
    // A rebased entry's steps are not history entries with the page's current query: going back would undo it. The
    // panel closes the record in place instead, taking it off the path so no later Back reopens it.
    if (history?.panelRebased && history.panelTop === kind && panels?.closeTopInPlace()) return;
    if (history?.panelTop === kind && (history.panelDepth ?? 0) > 0 && !history.panelRebased) {
      navigate(-1);
      return;
    }
    setParams(
      (prev) => {
        const query = new URLSearchParams(prev);
        query.delete(param);
        return query;
      },
      { replace: true, state: location.state },
    );
  }, [id, kind, param, location.state, navigate, setParams, panels]);

  return useMemo(() => ({ id, open, close }), [id, open, close]);
}

/** The record panel of this kind, or null outside the app shell (a test or harness without the provider). */
export function useOptionalRecordPanel(kind: RecordKind): RecordPanel | null {
  return useContext(RecordPanelContext)?.[kind] ?? null;
}

export function useRecordPanel(kind: RecordKind): RecordPanel {
  const context = useContext(RecordPanelContext);
  if (!context) throw new Error("useRecordPanel must be used within a RecordPanelProvider");
  return context[kind];
}

/** A plain click on a link to a record's page opens the record in the right panel instead, so the page
 *  stays where it was. The record already on top of the panel, a modified or non-left click, a link that
 *  opens a new tab, or a click outside the app shell still goes to the page. */
export function useRecordLinkClick(): (e: MouseEvent, href: string) => void {
  const records = useContext(RecordPanelContext);
  const panels = usePanelStack();
  return useCallback(
    (e: MouseEvent, href: string) => {
      if (!records) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if ((e.currentTarget as HTMLAnchorElement | null)?.target === "_blank") return;
      for (const kind of RECORD_KINDS) {
        const id = PANEL_STEPS[kind].path?.exec(href)?.[1];
        if (!id) continue;
        if (id === records[kind].id && panels?.top === kind) return;
        e.preventDefault();
        records[kind].open(id);
        return;
      }
    },
    [records, panels],
  );
}
